/**
 * Sentry initialisation for the Kantera web dashboard (React + Vite).
 *
 * Graceful degradation: when VITE_SENTRY_DSN is absent the module is a no-op —
 * the app loads and runs normally without any Sentry dependency.
 *
 * Call initWebSentry() once, before React renders, in main.tsx.
 */

import * as Sentry from '@sentry/react';
import type { Breadcrumb, ErrorEvent, Event as SentryEvent } from '@sentry/react';

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A path segment that could be a credential, replaced the way the API's Sentry scrub replaces it
 * (apps/api/src/services/sentry.ts, scrubPathSegment; keep the two rules the same): a UUID becomes
 * `:id`, 32+ hex characters or 16+ URL-safe characters with a digit or with both cases become
 * `:token`. One trailing file extension is ignored. Route words such as `create-account` survive.
 *
 * KAN-215 round 2 (SEC-1): the SMS digest's STOP link opens /sms/unsubscribe/<uuid>, and that page
 * fetches /api/sms-digest/stop/<uuid>. The token is the credential, and it sat in the page URL,
 * the transaction name, the fetch span and the fetch breadcrumb. The same holds for the email
 * unsubscribe link (/unsubscribe/<signed token>) and the family-circle invitation.
 */
function scrubPathSegment(segment: string): string {
  if (!segment || segment.startsWith(':')) return segment;
  const bare = segment.replace(/\.[A-Za-z0-9]{1,5}$/, '');
  if (UUID_SEGMENT.test(bare)) return ':id';
  if (/^[0-9a-f]{32,}$/i.test(bare)) return ':token';
  if (bare.length >= 16 && /^[A-Za-z0-9_.~-]+$/.test(bare)) {
    if (/\d/.test(bare) || (/[a-z]/.test(bare) && /[A-Z]/.test(bare))) return ':token';
  }
  return segment;
}

/**
 * A URL (or a bare path) without its query string or fragment, and with token-shaped path
 * segments replaced. Query strings are where this app carries some credentials —
 * /invite/create-account?token=… is the invitation itself — and paths carry others. Exported for
 * tests.
 */
export function scrubUrl(raw: string): string {
  const cut = raw.search(/[?#]/);
  const noQuery = cut === -1 ? raw : raw.slice(0, cut);
  const match = noQuery.match(/^([a-z][a-z0-9+.-]*:\/\/[^/]*)?(.*)$/i);
  const origin = match?.[1] ?? '';
  const path = match?.[2] ?? '';
  return `${origin}${path.split('/').map(scrubPathSegment).join('/')}`;
}

const withoutQuery = (url: unknown): unknown => (typeof url === 'string' ? scrubUrl(url) : url);

/**
 * beforeBreadcrumb: fetch / xhr breadcrumbs record the request URL, navigation
 * breadcrumbs the from/to page URLs — all without their query strings (KAN-115)
 * or token-shaped path segments (KAN-215).
 */
export function scrubBrowserBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (!breadcrumb.data) return breadcrumb;
  if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
    return {
      ...breadcrumb,
      data: { ...breadcrumb.data, url: withoutQuery(breadcrumb.data['url']) },
    };
  }
  if (breadcrumb.category === 'navigation') {
    return {
      ...breadcrumb,
      data: {
        ...breadcrumb.data,
        from: withoutQuery(breadcrumb.data['from']),
        to: withoutQuery(breadcrumb.data['to']),
      },
    };
  }
  return breadcrumb;
}

/**
 * beforeSend: the PII rule (no email, no IP) plus the page URL the SDK records,
 * without its query string (KAN-115).
 */
export function scrubBrowserEvent(event: ErrorEvent): ErrorEvent {
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
  }
  if (event.request) {
    event.request.url = withoutQuery(event.request.url) as string | undefined;
    delete event.request.query_string;
  }
  return event;
}

// Performance data (KAN-115 round 4, S1). beforeSend never sees transactions or
// spans, and a fetch span carries the full request URL — for the invite page's
// lookup, a LIVE invitation token.

/** Span attributes that hold a URL. */
const SPAN_URL_KEYS = ['url', 'http.url', 'url.full', 'http.target'];
/** Span attributes that hold only the query or fragment. */
const SPAN_QUERY_KEYS = ['http.query', 'url.query', 'http.fragment', 'url.fragment'];

/**
 * Every URL or path inside free text ("GET https://x/y?z", a pageload named after
 * /sms/unsubscribe/<token>) through scrubUrl. Text without a `/` is untouched.
 */
const withoutQueries = (text: string): string =>
  text.replace(/[a-z][a-z0-9+.-]*:\/\/[^\s]*|\/[^\s]*/gi, (url) => scrubUrl(url));

function scrubSpanData(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  for (const key of SPAN_URL_KEYS) data[key] = withoutQuery(data[key]);
  for (const key of SPAN_QUERY_KEYS) delete data[key];
  for (const key of SPAN_URL_KEYS) if (data[key] === undefined) delete data[key];
}

/** beforeSendSpan: a span's URLs and description, without query strings. */
export function scrubBrowserSpan<
  T extends { data?: Record<string, unknown>; description?: string },
>(span: T): T {
  scrubSpanData(span.data);
  if (typeof span.description === 'string') span.description = withoutQueries(span.description);
  return span;
}

/** beforeSendTransaction: the page URL, the root span, every child span, the name. */
export function scrubBrowserTransaction<T extends SentryEvent>(event: T): T {
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
  }
  if (event.request) {
    event.request.url = withoutQuery(event.request.url) as string | undefined;
    delete event.request.query_string;
  }
  if (typeof event.transaction === 'string') event.transaction = withoutQueries(event.transaction);
  scrubSpanData(event.contexts?.trace?.data as Record<string, unknown> | undefined);
  for (const span of event.spans ?? [])
    scrubBrowserSpan(span as { data?: Record<string, unknown> });
  return event;
}

/**
 * Initialise Sentry for the browser bundle.
 * Safe to call multiple times — Sentry's own SDK guards against double-init.
 */
export function initWebSentry(): void {
  const dsn = import.meta.env['VITE_SENTRY_DSN'] as string | undefined;

  if (!dsn) {
    // No DSN configured — skip initialisation entirely.
    return;
  }

  const environment =
    (import.meta.env['VITE_SENTRY_ENVIRONMENT'] as string | undefined) ||
    (import.meta.env['MODE'] as string | undefined) ||
    'development';

  const tracesSampleRate = (() => {
    const raw = import.meta.env['VITE_SENTRY_TRACES_SAMPLE_RATE'] as string | undefined;
    if (!raw) return 0.1;
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 0.1;
  })();

  Sentry.init({
    dsn,
    environment,
    tracesSampleRate,
    integrations: [Sentry.browserTracingIntegration()],

    // Strip PII before events leave the browser: email and IP must never appear
    // in Sentry per Kantera's PII policy, and neither may a query string (KAN-115).
    beforeSend: scrubBrowserEvent,
    beforeBreadcrumb: scrubBrowserBreadcrumb,
    beforeSendTransaction: scrubBrowserTransaction,
    beforeSendSpan: scrubBrowserSpan,
  });
}
